import { useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { BuscadorDePersonas } from '@/components/BuscadorDePersonas';
import {
  useBorrarHito, useHitos, useRegistrarHito, useTiposDeHito,
  type HitoRegistrado, type PersonaDelDirectorio,
} from './api';

/**
 * Hitos.tsx — lo que el portal no puede deducir solo.
 *
 * Un matrimonio o la llegada de un hijo no salen de ninguna fecha del maestro:
 * alguien los reporta y Bienestar decide si se celebran. Por eso viven aquí y no
 * en la tarea diaria que reparte cumpleaños.
 *
 * Son dos piezas de la misma opción: el formulario que registra y la tabla que
 * lista y da de baja.
 */

/* ── Registrar ─────────────────────────────────────────────────────────── */

export function RegistrarHito({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  // El catálogo solo se pide con el diálogo abierto: el componente se monta
  // siempre para que pueda animar su entrada.
  const tipos = useTiposDeHito(abierto);
  const registrar = useRegistrarHito();

  const [persona, setPersona] = useState<PersonaDelDirectorio | null>(null);
  const [tipo, setTipo] = useState('');
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [detalle, setDetalle] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [alColaborador, setAlColaborador] = useState(true);
  const [alFeed, setAlFeed] = useState(true);
  const [alEquipo, setAlEquipo] = useState(true);
  const [error, setError] = useState('');

  const tipoElegido = tipo || tipos.data?.[0]?.kind || '';

  function limpiar() {
    setPersona(null); setTipo(''); setFecha(hoy); setDetalle(''); setMensaje('');
    setAlColaborador(true); setAlFeed(true); setAlEquipo(true); setError('');
    registrar.reset();
  }

  function cerrar() { limpiar(); alCerrar(); }

  function guardar() {
    if (!persona) { setError('Selecciona el colaborador.'); return; }
    if (!tipoElegido) { setError('Elige el motivo.'); return; }
    setError('');
    registrar.mutate(
      {
        user_id: persona.id,
        kind: tipoElegido,
        fecha: fecha || null,
        detalle: detalle.trim(),
        message: mensaje.trim(),
        enviar_al_colaborador: alColaborador,
        publicar_feed: alFeed,
        avisar_equipo: alEquipo,
      },
      {
        onSuccess: (r) => {
          Utils.toast(
            `${r.message} · ${r.companeros_avisados} aviso${r.companeros_avisados === 1 ? '' : 's'} al equipo`,
            'success',
          );
          cerrar();
        },
        onError: (e: unknown) => setError((e as Error)?.message || 'No se pudo registrar.'),
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Registrar celebración"
      descripcion="Para hechos que el portal no puede saber solo: un matrimonio, la llegada de un hijo. Se genera la tarjeta, se le avisa al colaborador y se publica en el feed."
      ancho="max-w-xl"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={registrar.isPending}>
            <i className="bi bi-send" aria-hidden="true" />
            {registrar.isPending ? 'Enviando…' : 'Registrar y notificar'}
          </Button>
        </>
      }
    >
      <Field label="Colaborador" className="mb-3">
        <BuscadorDePersonas
          id="hito-persona"
          etiqueta="Buscar al colaborador por su nombre"
          elegida={persona}
          alElegir={setPersona}
          alSoltar={() => setPersona(null)}
        />
      </Field>

      <div className="mb-3 flex flex-wrap gap-3">
        <Field label="Motivo" className="min-w-[12rem] flex-1">
          <Select
            value={tipoElegido}
            onChange={(e) => setTipo(e.target.value)}
            disabled={tipos.isLoading}
          >
            {(tipos.data ?? []).map((t) => (
              <option key={t.kind} value={t.kind}>{t.etiqueta}</option>
            ))}
          </Select>
        </Field>
        <Field label="Fecha" className="min-w-[10rem] flex-1">
          {/* `max` en hoy: un hito es algo que YA pasó. Lo tenía el vanilla y se
              conserva — sin él se puede registrar un matrimonio del año que
              viene y la tarjeta sale hoy. */}
          <Input type="date" max={hoy} value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </Field>
      </div>

      {tipos.isError && (
        <p className="mb-3 text-3 text-danger-ink">
          No se pudieron cargar los motivos. Cierra y vuelve a abrir.
        </p>
      )}

      <Field label="Detalle" opcional className="mb-3">
        <Input
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
          placeholder="Ej: nombre del bebé"
        />
      </Field>
      <Field
        label="Mensaje de la tarjeta"
        opcional
        ayuda="Si lo dejas vacío se usa el mensaje por defecto."
        className="mb-3"
      >
        <Textarea rows={2} value={mensaje} onChange={(e) => setMensaje(e.target.value)} />
      </Field>

      <fieldset className="space-y-1.5">
        <legend className="sr-only">A quién se le avisa</legend>
        {([
          [alColaborador, setAlColaborador, 'Enviarle la tarjeta al colaborador'],
          [alFeed, setAlFeed, 'Publicar en el feed'],
          [alEquipo, setAlEquipo, 'Avisar al equipo por correo'],
        ] as const).map(([valor, poner, texto]) => (
          <label key={texto} className="flex items-center gap-2 text-2">
            <input
              type="checkbox"
              checked={valor}
              onChange={(e) => poner(e.target.checked)}
              className="size-4 accent-brand"
            />
            <span>{texto}</span>
          </label>
        ))}
      </fieldset>

      {error && <p role="alert" className="mt-3 text-3 text-danger-ink">{error}</p>}
    </Dialog>
  );
}

/* ── La tabla ──────────────────────────────────────────────────────────── */

export function TablaDeHitos({ activa, alRegistrar }: { activa: boolean; alRegistrar: () => void }) {
  const { data: filas = [], isLoading, isError, error, refetch } = useHitos(activa);
  const borrar = useBorrarHito();
  const [porBorrar, setPorBorrar] = useState<HitoRegistrado | null>(null);

  if (isLoading) return <p className="text-3 text-muted">Cargando…</p>;
  if (isError) {
    return <EstadoError err={error} que="los hitos registrados" onReintentar={() => { refetch(); }} />;
  }
  if (!filas.length) {
    return (
      <EstadoVacio
        icono="bi bi-flag"
        titulo="Aún no se ha registrado ningún hito"
        descripcion="Un matrimonio o el nacimiento de un hijo: cosas que el portal no puede saber solo."
        accion={{ texto: 'Registrar celebración', onClick: alRegistrar }}
      />
    );
  }

  return (
    <>
      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Colaborador</th><th>Motivo</th><th>Fecha</th><th>Avisos</th>
              <th><span className="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.id}>
                <td>{f.colaborador}</td>
                <td>{f.etiqueta}</td>
                <td className="text-2">{f.fecha}</td>
                <td className="text-2 text-muted">
                  <Aviso puesto={!!f.aviso_personal} que="colaborador" />
                  {' · '}
                  <Aviso puesto={!!f.aviso_equipo} que="equipo" />
                </td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Eliminar el hito de ${f.colaborador}`}
                    onClick={() => setPorBorrar(f)}
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        abierto={!!porBorrar}
        onCambio={(v) => { if (!v) setPorBorrar(null); }}
        titulo="Eliminar el registro"
        descripcion={
          porBorrar
            ? `Se quita el hito de ${porBorrar.colaborador} y su publicación del feed. Los correos ya enviados no se pueden recuperar.`
            : ''
        }
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!porBorrar) return;
          borrar.mutate(porBorrar.id, {
            onSuccess: (r) => { setPorBorrar(null); Utils.toast(r.message, 'success'); },
            onError: (e: unknown) =>
              Utils.toast((e as Error)?.message || 'No se pudo eliminar', 'error'),
          });
        }}
      />
    </>
  );
}

/** «✓ colaborador» o «— colaborador».
 *
 *  El vanilla ponía un icono suelto sin texto alternativo y un guion para el
 *  caso contrario, así que quien usa lector de pantalla oía «guion» y ya. */
function Aviso({ puesto, que }: { puesto: boolean; que: string }) {
  return (
    <span>
      <i
        className={puesto ? 'bi bi-check-circle-fill' : 'bi bi-dash-circle'}
        aria-hidden="true"
      />{' '}
      <span className="sr-only">{puesto ? 'Se avisó al' : 'No se avisó al'} </span>
      {que}
    </span>
  );
}
